import { useCallback, useEffect, useState } from 'react';

import { alterarCampo, excluirItem, incluirEnigma, incluirPista, listarCampos } from '../../conteudo/api/conteudo';
import type { CampoConteudo, CamposDaFase, ItemDaEstrutura } from '../../conteudo/api/conteudo';
import { UltimoEnigmaPainel } from '../../conteudo/ui/UltimoEnigmaPainel';
import { alterarMaterial, criarMaterial, enviarArquivo, excluirMaterial, listarMateriais } from '../../materiais/api/materiais';
import type { AlteracaoMaterial, Material } from '../../materiais/api/materiais';
import { alterarRegra, listarRegras } from '../../pontuacao/api/pontuacao';
import type { RegrasDaFase, ValorDaRegra } from '../../pontuacao/api/pontuacao';
import { LIXEIRA, mensagemDaFalha, SETA } from './comum';
import type { Aviso } from './comum';

const MAXIMO_DE_ENIGMAS = 8;

/**
 * "Pista: [k ▼] [+]" dentro do enigma: o seletor lista só as pistas criadas e o "+" abre a
 * seguinte (o servidor não tem teto). Só a pista marcada aparece; a lixeira fica no cartão dela.
 */
function SeletorDePista({ total, marcada, aoMarcar, aoIncluir, travado }: {
    total: number;
    marcada: number;
    aoMarcar: (k: number) => void;
    aoIncluir: () => void;
    travado: boolean;
}) {
    return (
        <div className="seletor-enigma-config">
            <label>
                <span>Pista:</span>
                <select aria-label="Pista" value={String(marcada)} onChange={(evento) => aoMarcar(Number(evento.target.value))}>
                    {Array.from({ length: total }, (_, i) => (
                        <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                    ))}
                </select>
            </label>
            <button type="button" className="botao-novo-config" aria-label="Nova Pista" title="Nova Pista" disabled={travado} onClick={aoIncluir}>
                <span aria-hidden="true">+</span>
            </button>
        </div>
    );
}

/** Enigma ainda não criado: vai ao servidor no "Salvar Alterações". */
interface EnigmaNovo {
    tempId: string;
    charada: string;
    quantidade: number;
    pistas: { texto: string; codigo: string }[];
}

const pistaVazia = () => ({ texto: '', codigo: '' });


interface PistaDaTela {
    id: string;
    k: number;
    texto: CampoConteudo | undefined;
    codigo: CampoConteudo | undefined;
    item: ItemDaEstrutura | undefined;
}

interface EnigmaDaTela {
    id: string;
    n: number;
    charada: CampoConteudo;
    pistas: PistaDaTela[];
    item: ItemDaEstrutura | undefined;
}

/** Os enigmas e as pistas de cada um, a partir dos campos do conteúdo (`local` "Enigma N › Pista k"). */
function montarEnigmas(dados: CamposDaFase): EnigmaDaTela[] {
    const itens = new Map((dados.estrutura?.itens ?? []).map((item) => [item.id, item]));
    const enigmas = dados.campos
        .filter((campo) => campo.entidade === 'enigma' && campo.campo === 'texto_charada')
        .map((charada) => {
            const pistas = new Map<string, PistaDaTela>();
            for (const campo of dados.campos) {
                if (campo.entidade !== 'pista' || !campo.local.startsWith(`${charada.local} › Pista `)) continue;
                const k = Number(campo.local.split('Pista ').pop());
                const pista = pistas.get(campo.id) ?? { id: campo.id, k, texto: undefined, codigo: undefined, item: itens.get(campo.id) };
                if (campo.campo === 'enunciado') pista.texto = campo;
                if (campo.campo === 'valor_esperado') pista.codigo = campo;
                pistas.set(campo.id, pista);
            }
            return {
                id: charada.id,
                n: Number(charada.local.replace('Enigma ', '')),
                charada,
                pistas: [...pistas.values()].sort((a, b) => a.k - b.k),
                item: itens.get(charada.id),
            };
        });
    return enigmas.sort((a, b) => a.n - b.n);
}

const chaveDoCampo = (campo: CampoConteudo) => `${campo.entidade}:${campo.id}:${campo.campo}`;
const chaveDaPistaNova = (enigmaId: string, k: number) => `${enigmaId}:${k}`;

/** As regras do Scan Pixel na tela: pontos e bônus, mais um tipo de ranking para a fase toda. */
const REGRAS_DE_PONTOS = [
    { chave: 'pontos_por_enigma', rotulo: 'Enigma correto' },
    { chave: 'bonus_colocacao_1', rotulo: 'Bônus de 1º Lugar' },
    { chave: 'bonus_colocacao_2', rotulo: 'Bônus de 2º Lugar' },
    { chave: 'bonus_colocacao_3', rotulo: 'Bônus de 3º Lugar' },
] as const;

interface MaterialNovo {
    tempId: string;
    titulo: string;
    enigma: string;
}

/**
 * A aba Scan Pixel de Configurações, no modelo enviado: cada enigma com a charada e as pistas
 * (texto e código), a Pontuação e os Materiais. Tudo fica pendente até "Salvar Alterações"; as
 * lixeiras confirmam e valem na hora. As rotas são as de sempre (conteúdo, pontuação, materiais).
 */
export function ScanPixelConfiguracao({ csrfToken }: { csrfToken: string }) {
    const [dados, setDados] = useState<CamposDaFase | null>(null);
    const [regras, setRegras] = useState<RegrasDaFase | null>(null);
    const [materiais, setMateriais] = useState<Material[] | null>(null);
    const [falhouCarregar, setFalhouCarregar] = useState(false);

    const [rascunho, setRascunho] = useState<Record<string, string>>({});
    const [quantidade, setQuantidade] = useState<Record<string, number>>({});
    const [pistasNovas, setPistasNovas] = useState<Record<string, { texto: string; codigo: string }>>({});
    const [regrasRascunho, setRegrasRascunho] = useState<Record<string, string>>({});
    const [materiaisRascunho, setMateriaisRascunho] = useState<Record<string, { titulo?: string; enigma?: string }>>({});
    const [materiaisNovos, setMateriaisNovos] = useState<MaterialNovo[]>([]);
    const [enigmasNovos, setEnigmasNovos] = useState<EnigmaNovo[]>([]);
    /** O número do enigma marcado no seletor: só ele aparece (os outros guardam o que foi digitado). */
    const [selecionado, setSelecionado] = useState(1);
    /** A pista marcada no seletor de cada enigma (pelo id, ou pelo `tempId` do enigma novo). */
    const [pistaMarcada, setPistaMarcada] = useState<Record<string, number>>({});
    const [arquivos, setArquivos] = useState<Record<string, File>>({});

    const [pontuacaoAberta, setPontuacaoAberta] = useState(true);
    const [materiaisAbertos, setMateriaisAbertos] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [aviso, setAviso] = useState<Aviso | null>(null);

    const carregar = useCallback(async () => {
        try {
            const [conteudo, todasAsRegras, lista] = await Promise.all([listarCampos('scanpixel'), listarRegras(), listarMateriais()]);
            setDados(conteudo);
            setRegras(todasAsRegras.find((fase) => fase.fase === 'scanpixel') ?? null);
            setMateriais(lista);
            setFalhouCarregar(false);
        } catch {
            setFalhouCarregar(true);
        }
    }, []);

    useEffect(() => {
        void carregar();
    }, [carregar]);

    function descartar() {
        setRascunho({});
        setQuantidade({});
        setPistasNovas({});
        setRegrasRascunho({});
        setMateriaisRascunho({});
        setMateriaisNovos([]);
        setEnigmasNovos([]);
        setArquivos({});
    }

    if (falhouCarregar) {
        return (
            <p className="estado-variaveis mensagem-login">
                Não foi possível carregar o Scan Pixel.{' '}
                <button className="botao-texto" type="button" onClick={() => void carregar()}>Tentar novamente</button>
            </p>
        );
    }
    if (!dados || !regras || !materiais) return <p className="estado-variaveis">Carregando o Scan Pixel…</p>;

    const enigmas = montarEnigmas(dados);
    const totalDeEnigmas = enigmas.length + enigmasNovos.length;
    // Descartar um enigma novo ou reler com menos enigmas não deixa o seletor apontando para o vazio.
    const marcado = selecionado >= 1 && selecionado <= totalDeEnigmas ? selecionado : 1;
    const travado = !dados.editavel;
    const regrasTravadas = !regras.editavel;
    const abertas = [...new Set([...dados.turmas_com_fase_aberta, ...regras.turmas_com_fase_aberta])];

    const valorDoCampo = (campo: CampoConteudo | undefined) =>
        campo ? (rascunho[chaveDoCampo(campo)] ?? String(campo.valor ?? '')) : '';
    const mudarCampo = (campo: CampoConteudo | undefined, valor: string) => {
        if (campo) setRascunho((atual) => ({ ...atual, [chaveDoCampo(campo)]: valor }));
    };
    const quantidadeDe = (enigma: EnigmaDaTela) => quantidade[enigma.id] ?? enigma.pistas.length;
    const valorDaRegra = (chave: string) => regrasRascunho[chave] ?? String(regras.regras[chave] ?? '');

    async function excluirNaHora(pergunta: string, acao: () => Promise<void>) {
        if (!window.confirm(pergunta)) return;
        setAviso(null);
        try {
            await acao();
            setAviso({ tipo: 'sucesso', texto: 'Excluído.' });
        } catch (erro) {
            setAviso({ tipo: 'erro', texto: mensagemDaFalha(erro) });
        }
        await carregar();
    }

    async function salvar() {
        if (!dados || !regras || !materiais) return;
        setAviso(null);

        // Pistas novas precisam de texto e código antes de qualquer gravação.
        const novas: { enigma: EnigmaDaTela; k: number; texto: string; codigo: string }[] = [];
        const removidas: { enigma: EnigmaDaTela; pista: PistaDaTela }[] = [];
        for (const enigma of enigmas) {
            const desejada = quantidadeDe(enigma);
            for (let k = enigma.pistas.length + 1; k <= desejada; k++) {
                const nova = pistasNovas[chaveDaPistaNova(enigma.id, k)] ?? { texto: '', codigo: '' };
                if (!nova.texto.trim() || !nova.codigo.trim()) {
                    // Só o enigma marcado aparece: o seletor vai até o que falta.
                    setSelecionado(enigma.n);
                    setPistaMarcada((atual) => ({ ...atual, [enigma.id]: k }));
                    setAviso({ tipo: 'erro', texto: `Preencha o texto e o código da Pista ${k} do Enigma ${enigma.n}.` });
                    return;
                }
                novas.push({ enigma, k, texto: nova.texto.trim(), codigo: nova.codigo.trim() });
            }
            for (const pista of enigma.pistas.slice(desejada)) removidas.push({ enigma, pista });
        }
        // Enigma novo: a charada e todas as pistas pedidas, antes de qualquer gravação.
        for (const [indice, novo] of enigmasNovos.entries()) {
            const pistas = novo.pistas.slice(0, novo.quantidade);
            const incompleta = pistas.findIndex((pista) => !pista.texto.trim() || !pista.codigo.trim());
            if (!novo.charada.trim() || incompleta >= 0) {
                setSelecionado(enigmas.length + indice + 1);
                if (incompleta >= 0) setPistaMarcada((atual) => ({ ...atual, [novo.tempId]: incompleta + 1 }));
                setAviso({ tipo: 'erro', texto: `Preencha a charada e as pistas do Enigma ${enigmas.length + indice + 1}.` });
                return;
            }
        }
        const semNome = materiaisNovos.find((novo) => !novo.titulo.trim());
        if (semNome) {
            setAviso({ tipo: 'erro', texto: 'Dê um nome ao material novo.' });
            return;
        }
        if (removidas.length > 0) {
            const lista = removidas.map(({ enigma, pista }) => `Pista ${pista.k} do Enigma ${enigma.n}`).join(', ');
            if (!window.confirm(`Remover ${lista}? A pista é apagada e isso não pode ser desfeito.`)) return;
        }

        setSalvando(true);
        try {
            for (const campo of dados.campos) {
                const digitado = rascunho[chaveDoCampo(campo)];
                if (digitado === undefined || digitado === String(campo.valor ?? '')) continue;
                await alterarCampo(campo, digitado, campo.valor, csrfToken);
            }
            for (const { pista } of removidas) await excluirItem({ entidade: 'pista', id: pista.id }, csrfToken);
            for (const nova of novas) {
                await incluirPista(nova.enigma.id, { enunciado: nova.texto, valor_esperado: nova.codigo }, csrfToken);
            }
            // O enigma nasce com a charada e a primeira pista; as demais entram no id novo.
            for (const novo of enigmasNovos) {
                const [primeira, ...demais] = novo.pistas.slice(0, novo.quantidade);
                const criado = await incluirEnigma(
                    { texto_charada: novo.charada.trim(), enunciado: primeira.texto.trim(), valor_esperado: primeira.codigo.trim() },
                    csrfToken,
                );
                for (const pista of demais) {
                    await incluirPista(criado.id, { enunciado: pista.texto.trim(), valor_esperado: pista.codigo.trim() }, csrfToken);
                }
            }
            for (const [chave, digitado] of Object.entries(regrasRascunho)) {
                if (digitado === String(regras.regras[chave] ?? '')) continue;
                const valor: ValorDaRegra = chave === 'destino' ? (digitado as ValorDaRegra) : Number(digitado);
                await alterarRegra('scanpixel', chave, valor, csrfToken);
            }
            for (const material of materiais) {
                const mudado = materiaisRascunho[material.id] ?? {};
                const mudanca: AlteracaoMaterial = {};
                if (mudado.titulo !== undefined && mudado.titulo.trim() !== material.titulo) mudanca.titulo = mudado.titulo.trim();
                if (mudado.enigma !== undefined && (mudado.enigma === '' ? null : Number(mudado.enigma)) !== material.enigma_ordem) {
                    mudanca.enigma_ordem = mudado.enigma === '' ? null : Number(mudado.enigma);
                }
                if (Object.keys(mudanca).length > 0) await alterarMaterial(material.id, mudanca, csrfToken);
                if (arquivos[material.id]) await enviarArquivo(material.id, arquivos[material.id], csrfToken);
            }
            for (const novo of materiaisNovos) {
                const criado = await criarMaterial(
                    { titulo: novo.titulo.trim(), enigma_ordem: novo.enigma === '' ? null : Number(novo.enigma) },
                    csrfToken,
                );
                if (arquivos[novo.tempId]) await enviarArquivo(criado.id, arquivos[novo.tempId], csrfToken);
            }
            descartar();
            setAviso({ tipo: 'sucesso', texto: 'Alterações salvas.' });
        } catch (erro) {
            setAviso({ tipo: 'erro', texto: mensagemDaFalha(erro) });
        } finally {
            setSalvando(false);
            await carregar();
        }
    }

    const mudarEnigmaNovo = (tempId: string, mudanca: (novo: EnigmaNovo) => EnigmaNovo) =>
        setEnigmasNovos((atual) => atual.map((novo) => (novo.tempId === tempId ? mudanca(novo) : novo)));
    /** Tira a pista nova `k` (ainda não gravada) do enigma: as seguintes sobem uma posição. */
    const descartarPistaNova = (enigma: EnigmaDaTela, k: number) => {
        const desejada = quantidadeDe(enigma);
        setPistasNovas((atual) => {
            const proximo = { ...atual };
            for (let j = k; j < desejada; j++) {
                const seguinte = atual[chaveDaPistaNova(enigma.id, j + 1)];
                if (seguinte) proximo[chaveDaPistaNova(enigma.id, j)] = seguinte;
                else delete proximo[chaveDaPistaNova(enigma.id, j)];
            }
            delete proximo[chaveDaPistaNova(enigma.id, desejada)];
            return proximo;
        });
        setQuantidade((atual) => ({ ...atual, [enigma.id]: desejada - 1 }));
        setPistaMarcada((atual) => ({ ...atual, [enigma.id]: Math.min(k, desejada - 1) }));
    };
    const podeIncluirEnigma = !travado
        && dados.estrutura?.pode_incluir !== false
        && enigmas.length + enigmasNovos.length < MAXIMO_DE_ENIGMAS;
    const motivoSemIncluir = dados.estrutura?.motivo_incluir
        ?? (enigmas.length + enigmasNovos.length >= MAXIMO_DE_ENIGMAS ? `O Scan Pixel tem no máximo ${MAXIMO_DE_ENIGMAS} enigmas.` : undefined);

    const opcoesDeEnigma = enigmas.map((enigma) => (
        <option key={enigma.id} value={String(enigma.n)}>Enigma {enigma.n}</option>
    ));
    const linhasDeMateriais = [
        ...materiais.map((material) => ({
            chave: material.id,
            titulo: materiaisRascunho[material.id]?.titulo ?? material.titulo,
            enigma: materiaisRascunho[material.id]?.enigma ?? (material.enigma_ordem === null ? '' : String(material.enigma_ordem)),
            arquivo: arquivos[material.id]?.name ?? material.arquivo_nome,
            existente: material,
        })),
        ...materiaisNovos.map((novo) => ({
            chave: novo.tempId, titulo: novo.titulo, enigma: novo.enigma, arquivo: arquivos[novo.tempId]?.name ?? null, existente: null,
        })),
    ];
    const mudarMaterial = (chave: string, existente: Material | null, mudanca: { titulo?: string; enigma?: string }) => {
        if (existente) setMateriaisRascunho((atual) => ({ ...atual, [chave]: { ...atual[chave], ...mudanca } }));
        else setMateriaisNovos((atual) => atual.map((novo) => (novo.tempId === chave ? { ...novo, ...mudanca } : novo)));
    };

    return (
        <div className="config-fase">
            {abertas.length > 0 && (
                <p className="aviso-fase-aberta" role="status">
                    A fase Scan Pixel está aberta na turma {abertas.join(', ')}: só leitura até ela fechar.
                </p>
            )}

            <div className="seletor-enigma-config">
                <label>
                    <span>Enigma:</span>
                    <select aria-label="Enigma" value={String(marcado)} onChange={(evento) => setSelecionado(Number(evento.target.value))}>
                        {Array.from({ length: totalDeEnigmas }, (_, i) => (
                            <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                        ))}
                    </select>
                </label>
                <button
                    type="button"
                    className="botao-novo-config"
                    disabled={!podeIncluirEnigma}
                    title={podeIncluirEnigma ? undefined : motivoSemIncluir}
                    onClick={() => {
                        setEnigmasNovos((atual) => [
                            ...atual,
                            { tempId: `enigma-${Date.now()}-${atual.length}`, charada: '', quantidade: 1, pistas: [pistaVazia()] },
                        ]);
                        setSelecionado(totalDeEnigmas + 1);
                    }}
                >
                    <span aria-hidden="true">+</span> Novo Enigma
                </button>
            </div>

            {enigmas.filter((enigma) => enigma.n === marcado).map((enigma) => {
                const desejada = quantidadeDe(enigma);
                const marcada = Math.min(pistaMarcada[enigma.id] ?? 1, desejada);
                return (
                    <section key={enigma.id} className="cartao-config" aria-labelledby={`enigma-${enigma.id}`}>
                        <div className="cabecalho-cartao-config">
                            <h3 id={`enigma-${enigma.id}`}>Enigma {enigma.n}</h3>
                            {enigma.item?.pode_excluir && !travado && (
                                <button
                                    type="button"
                                    className="lixeira-config"
                                    aria-label={`Excluir Enigma ${enigma.n}`}
                                    onClick={() => void excluirNaHora(
                                        `Excluir o Enigma ${enigma.n} e as pistas dele? Isso não pode ser desfeito.`,
                                        () => excluirItem({ entidade: 'enigma', id: enigma.id }, csrfToken),
                                    )}
                                >
                                    {LIXEIRA}
                                </button>
                            )}
                        </div>
                        <label className="campo-config">
                            Charada
                            <textarea
                                rows={3}
                                value={valorDoCampo(enigma.charada)}
                                disabled={travado}
                                onChange={(evento) => mudarCampo(enigma.charada, evento.target.value)}
                            />
                        </label>
                        <SeletorDePista
                            total={desejada}
                            marcada={marcada}
                            travado={travado}
                            aoMarcar={(k) => setPistaMarcada((atual) => ({ ...atual, [enigma.id]: k }))}
                            aoIncluir={() => {
                                setQuantidade((atual) => ({ ...atual, [enigma.id]: desejada + 1 }));
                                setPistaMarcada((atual) => ({ ...atual, [enigma.id]: desejada + 1 }));
                            }}
                        />

                        {[marcada].map((k) => {
                            const existente = enigma.pistas.find((pista) => pista.k === k);
                            const chaveNova = chaveDaPistaNova(enigma.id, k);
                            const nova = pistasNovas[chaveNova] ?? { texto: '', codigo: '' };
                            const mudarNova = (mudanca: Partial<typeof nova>) =>
                                setPistasNovas((atual) => ({ ...atual, [chaveNova]: { ...nova, ...mudanca } }));
                            return (
                                <section key={k} className="cartao-pista" aria-labelledby={`pista-${enigma.id}-${k}`}>
                                    <div className="cabecalho-cartao-config">
                                        <h4 id={`pista-${enigma.id}-${k}`}>Pista {k}</h4>
                                        {existente?.item?.pode_excluir && !travado && (
                                            <button
                                                type="button"
                                                className="lixeira-config"
                                                aria-label={`Excluir Pista ${k} do Enigma ${enigma.n}`}
                                                onClick={() => void excluirNaHora(
                                                    `Excluir a Pista ${k} do Enigma ${enigma.n}? Isso não pode ser desfeito.`,
                                                    () => excluirItem({ entidade: 'pista', id: existente.id }, csrfToken),
                                                )}
                                            >
                                                {LIXEIRA}
                                            </button>
                                        )}
                                        {!existente && !travado && (
                                            <button
                                                type="button"
                                                className="lixeira-config"
                                                aria-label={`Descartar Pista ${k} do Enigma ${enigma.n}`}
                                                onClick={() => descartarPistaNova(enigma, k)}
                                            >
                                                {LIXEIRA}
                                            </button>
                                        )}
                                    </div>
                                    <label className="campo-config">
                                        Texto
                                        <textarea
                                            rows={3}
                                            value={existente ? valorDoCampo(existente.texto) : nova.texto}
                                            disabled={travado}
                                            onChange={(evento) => (existente
                                                ? mudarCampo(existente.texto, evento.target.value)
                                                : mudarNova({ texto: evento.target.value }))}
                                        />
                                    </label>
                                    <label className="campo-config">
                                        Código
                                        <input
                                            type="text"
                                            value={existente ? valorDoCampo(existente.codigo) : nova.codigo}
                                            disabled={travado}
                                            onChange={(evento) => (existente
                                                ? mudarCampo(existente.codigo, evento.target.value)
                                                : mudarNova({ codigo: evento.target.value }))}
                                        />
                                    </label>
                                </section>
                            );
                        })}
                    </section>
                );
            })}

            {enigmasNovos.map((novo, indice) => {
                const n = enigmas.length + indice + 1;
                if (n !== marcado) return null;
                const marcadaDoNovo = Math.min(pistaMarcada[novo.tempId] ?? 1, novo.quantidade);
                return (
                    <section key={novo.tempId} className="cartao-config cartao-config-novo" aria-labelledby={`enigma-novo-${novo.tempId}`}>
                        <div className="cabecalho-cartao-config">
                            <h3 id={`enigma-novo-${novo.tempId}`}>Enigma {n}</h3>
                            <button
                                type="button"
                                className="lixeira-config"
                                aria-label={`Descartar Enigma ${n}`}
                                onClick={() => setEnigmasNovos((atual) => atual.filter((outro) => outro.tempId !== novo.tempId))}
                            >
                                {LIXEIRA}
                            </button>
                        </div>
                        <label className="campo-config">
                            Charada
                            <textarea
                                rows={3}
                                value={novo.charada}
                                onChange={(evento) => mudarEnigmaNovo(novo.tempId, (atual) => ({ ...atual, charada: evento.target.value }))}
                            />
                        </label>
                        <SeletorDePista
                            total={novo.quantidade}
                            marcada={marcadaDoNovo}
                            travado={false}
                            aoMarcar={(k) => setPistaMarcada((atual) => ({ ...atual, [novo.tempId]: k }))}
                            aoIncluir={() => {
                                mudarEnigmaNovo(novo.tempId, (atual) => ({
                                    ...atual,
                                    quantidade: atual.quantidade + 1,
                                    pistas: [...atual.pistas.slice(0, atual.quantidade), pistaVazia()],
                                }));
                                setPistaMarcada((atual) => ({ ...atual, [novo.tempId]: novo.quantidade + 1 }));
                            }}
                        />
                        {novo.pistas.slice(0, novo.quantidade).map((pista, i) => {
                            const k = i + 1;
                            if (k !== marcadaDoNovo) return null;
                            const mudarPista = (mudanca: Partial<typeof pista>) =>
                                mudarEnigmaNovo(novo.tempId, (atual) => ({
                                    ...atual,
                                    pistas: atual.pistas.map((outra, j) => (j === i ? { ...outra, ...mudanca } : outra)),
                                }));
                            return (
                                <section key={k} className="cartao-pista" aria-labelledby={`pista-novo-${novo.tempId}-${k}`}>
                                    <div className="cabecalho-cartao-config">
                                        <h4 id={`pista-novo-${novo.tempId}-${k}`}>Pista {k}</h4>
                                        {novo.quantidade > 1 && (
                                            <button
                                                type="button"
                                                className="lixeira-config"
                                                aria-label={`Descartar Pista ${k} do Enigma ${n}`}
                                                onClick={() => {
                                                    mudarEnigmaNovo(novo.tempId, (atual) => ({
                                                        ...atual,
                                                        quantidade: atual.quantidade - 1,
                                                        pistas: atual.pistas.filter((_, j) => j !== i),
                                                    }));
                                                    setPistaMarcada((atual) => ({ ...atual, [novo.tempId]: Math.min(k, novo.quantidade - 1) }));
                                                }}
                                            >
                                                {LIXEIRA}
                                            </button>
                                        )}
                                    </div>
                                    <label className="campo-config">
                                        Texto
                                        <textarea rows={3} value={pista.texto} onChange={(evento) => mudarPista({ texto: evento.target.value })} />
                                    </label>
                                    <label className="campo-config">
                                        Código
                                        <input type="text" value={pista.codigo} onChange={(evento) => mudarPista({ codigo: evento.target.value })} />
                                    </label>
                                </section>
                            );
                        })}
                    </section>
                );
            })}

            {/* AP12: o campo de cada PISTA do último enigma. Ficava na tela Variáveis, que saiu do menu. */}
            <UltimoEnigmaPainel csrfToken={csrfToken} />

            <section className="cartao-config" aria-labelledby="config-pontuacao">
                <div className="cabecalho-cartao-config">
                    <h3 id="config-pontuacao">Pontuação</h3>
                    <button
                        type="button"
                        className={`seta-config${pontuacaoAberta ? ' seta-config-aberta' : ''}`}
                        aria-label={pontuacaoAberta ? 'Recolher Pontuação' : 'Abrir Pontuação'}
                        aria-expanded={pontuacaoAberta}
                        onClick={() => setPontuacaoAberta((aberta) => !aberta)}
                    >
                        {SETA}
                    </button>
                </div>
                {pontuacaoAberta && (
                    <div className="grade-pontuacao">
                        {REGRAS_DE_PONTOS.slice(0, 1).map(({ chave, rotulo }) => (
                            <label key={chave} className="campo-config">
                                {rotulo}
                                <input
                                    type="number"
                                    min={0}
                                    value={valorDaRegra(chave)}
                                    disabled={regrasTravadas}
                                    onChange={(evento) => setRegrasRascunho((atual) => ({ ...atual, [chave]: evento.target.value }))}
                                />
                            </label>
                        ))}
                        <label className="campo-config">
                            Tipo do Ranking
                            <select
                                value={valorDaRegra('destino')}
                                disabled={regrasTravadas}
                                onChange={(evento) => setRegrasRascunho((atual) => ({ ...atual, destino: evento.target.value }))}
                            >
                                <option value="grupo">Grupos</option>
                                <option value="individual">Individual</option>
                            </select>
                        </label>
                        {REGRAS_DE_PONTOS.slice(1).map(({ chave, rotulo }) => (
                            <label key={chave} className="campo-config">
                                {rotulo}
                                <input
                                    type="number"
                                    min={0}
                                    value={valorDaRegra(chave)}
                                    disabled={regrasTravadas}
                                    onChange={(evento) => setRegrasRascunho((atual) => ({ ...atual, [chave]: evento.target.value }))}
                                />
                            </label>
                        ))}
                        <p className="nota-config">O tipo do ranking vale para a fase toda, inclusive os bônus.</p>
                    </div>
                )}
            </section>

            <section className="cartao-config" aria-labelledby="config-materiais">
                <div className="cabecalho-cartao-config">
                    <div className="titulo-com-botao">
                        <h3 id="config-materiais">Materiais</h3>
                        <button
                            type="button"
                            className="botao-novo-config"
                            disabled={travado}
                            onClick={() => setMateriaisNovos((atual) => [...atual, { tempId: `novo-${Date.now()}-${atual.length}`, titulo: '', enigma: '' }])}
                        >
                            <span aria-hidden="true">+</span> Novo Material
                        </button>
                    </div>
                    <button
                        type="button"
                        className={`seta-config${materiaisAbertos ? ' seta-config-aberta' : ''}`}
                        aria-label={materiaisAbertos ? 'Recolher Materiais' : 'Abrir Materiais'}
                        aria-expanded={materiaisAbertos}
                        onClick={() => setMateriaisAbertos((aberto) => !aberto)}
                    >
                        {SETA}
                    </button>
                </div>
                {materiaisAbertos && (
                    <table className="tabela-config">
                        <thead>
                            <tr>
                                <th scope="col">Nome</th>
                                <th scope="col">PDF</th>
                                <th scope="col">Liberado por</th>
                                <th scope="col">Ação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {linhasDeMateriais.map((linha, indice) => {
                                const numero = indice + 1;
                                return (
                                    <tr key={linha.chave}>
                                        <td>
                                            <input
                                                type="text"
                                                aria-label={`Nome do material ${numero}`}
                                                value={linha.titulo}
                                                disabled={travado}
                                                onChange={(evento) => mudarMaterial(linha.chave, linha.existente, { titulo: evento.target.value })}
                                            />
                                        </td>
                                        <td className="arquivo-config">
                                            <span>{linha.arquivo ?? 'Sem PDF'}</span>
                                            <label className="botao-arquivo-config">
                                                {linha.arquivo ? 'Trocar' : 'Enviar PDF'}
                                                <input
                                                    type="file"
                                                    accept="application/pdf"
                                                    aria-label={`PDF do material ${numero}`}
                                                    disabled={travado}
                                                    onChange={(evento) => {
                                                        const arquivo = evento.target.files?.[0];
                                                        if (arquivo) setArquivos((atual) => ({ ...atual, [linha.chave]: arquivo }));
                                                    }}
                                                />
                                            </label>
                                        </td>
                                        <td>
                                            <select
                                                aria-label={`Liberado por do material ${numero}`}
                                                value={linha.enigma}
                                                disabled={travado}
                                                onChange={(evento) => mudarMaterial(linha.chave, linha.existente, { enigma: evento.target.value })}
                                            >
                                                <option value="">Sempre liberado</option>
                                                {opcoesDeEnigma}
                                            </select>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="lixeira-config"
                                                aria-label={`Excluir material ${numero}`}
                                                disabled={travado}
                                                onClick={() => {
                                                    const existente = linha.existente;
                                                    if (!existente) {
                                                        setMateriaisNovos((atual) => atual.filter((novo) => novo.tempId !== linha.chave));
                                                        return;
                                                    }
                                                    void excluirNaHora(
                                                        `Excluir o material "${existente.titulo}" e o PDF dele? Isso não pode ser desfeito.`,
                                                        () => excluirMaterial(existente.id, csrfToken),
                                                    );
                                                }}
                                            >
                                                {LIXEIRA}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
                {materiaisAbertos && linhasDeMateriais.length === 0 && <p className="nota-config">Nenhum material cadastrado.</p>}
            </section>

            {aviso && (
                <p className={aviso.tipo === 'erro' ? 'mensagem-login aviso-cadastro' : 'mensagem-salvo'} role={aviso.tipo === 'erro' ? 'alert' : 'status'}>
                    {aviso.texto}
                </p>
            )}

            <div className="rodape-config">
                <button type="button" className="botao-cancelar-config" onClick={descartar} disabled={salvando}>
                    Cancelar
                </button>
                <button type="button" className="botao-salvar-config" onClick={() => void salvar()} disabled={salvando || travado}>
                    {salvando ? 'Salvando…' : 'Salvar Alterações'}
                </button>
            </div>
        </div>
    );
}
